import { useState, useEffect, useCallback } from "react";
import apiClient from "utils/apiClient";

const useSecurityQuestions = () => {
  const [securityQns, setSecurityQns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination info
  const [pagination, setPagination] = useState({
    page: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
  });

  const fetchSecurityQuestions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const pageSize = 50; // You can make this configurable if needed
      const baseUrl = "/identity-service/api/v1/security-questions";

      // 1. Fetch first page to know totalPages
      const firstResponse = await apiClient.get(
        `${baseUrl}?page=0&size=${pageSize}`
      );

      const {
        content: firstContent = [],
        totalPages = 1,
        totalElements = 0,
      } = firstResponse.data;

      let allQuestions = [...firstContent];

      // 2. If more than 1 page → fetch all remaining pages in parallel
      if (totalPages > 1) {
        const remainingPromises = [];
        for (let page = 1; page < totalPages; page++) {
          remainingPromises.push(
            apiClient.get(`${baseUrl}?page=${page}&size=${pageSize}`)
          );
        }

        const responses = await Promise.all(remainingPromises);
        responses.forEach((res) => {
          allQuestions = allQuestions.concat(res.data.content || []);
        });
      }

      setSecurityQns(allQuestions);

      // Final pagination reflects the FULL dataset
      setPagination({
        page: 0,
        size: allQuestions.length,
        totalElements: allQuestions.length,
        totalPages: 1,        // Now it's just one "virtual" page
        first: true,
        last: true,
      });

    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to load security questions";
      console.error("[useSecurityQuestions] Error:", err);
      setError(message);
      setSecurityQns([]);
      setPagination({
        page: 0,
        size: 0,
        totalElements: 0,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new security question
  const addSecurityQuestion = useCallback(
    async (questionData) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.post(
          "/identity-service/api/v1/security-questions",
          questionData
        );
        // Refresh current page after add
        await fetchSecurityQuestions(pagination.page, pagination.size);
        return response.data;
      } catch (err) {
        const message = err.response?.data?.message || err.message;
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [fetchSecurityQuestions, pagination.page, pagination.size]
  );

  // Update a security question by ID
  const updateSecurityQuestion = useCallback(
    async (id, questionData) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.patch(
          `/identity-service/api/v1/security-questions/${id}`,
          questionData
        );
        await fetchSecurityQuestions(pagination.page, pagination.size);
        return response.data;
      } catch (err) {
        const message = err.response?.data?.message;
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [fetchSecurityQuestions, pagination.page, pagination.size]
  );

  // Delete a security question by ID
  const deleteSecurityQuestion = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await apiClient.delete(`/identity-service/api/v1/security-questions/${id}`);
        await fetchSecurityQuestions(); // This reloads everything — perfect for your use case
      } catch (err) {
        const message = err.response?.data?.message || "Delete failed";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchSecurityQuestions]
  );

  // Initial fetch once on mount, avoid repeated fetch if pagination changes
  useEffect(() => {
    fetchSecurityQuestions(0, 20);
  }, [fetchSecurityQuestions]);

  return {
    securityQns,
    loading,
    error,
    pagination,
    fetchSecurityQuestions,
    addSecurityQuestion,
    updateSecurityQuestion,
    deleteSecurityQuestion,
  };
};

export default useSecurityQuestions;
